import { useCallback, useEffect, useRef, useState } from 'react';
import { listOtoAttachments, uploadOtoAttachment } from '../services/api/oto';
import { formatApiError } from '../utils/apiError';

export const useOtoAttachments = (sessionId, ensureSession) => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');
  const [archiveReview, setArchiveReview] = useState(null);
  const current = useRef(sessionId);
  const fetchVersion = useRef(0);
  const pending = useRef(false);
  const alive = useRef(true);
  const refresh = useCallback(async id => {
    if (!id) return;
    const version = ++fetchVersion.current;
    try {
      const { data } = await listOtoAttachments(id);
      if (alive.current && current.current === id && version === fetchVersion.current) setItems(data.items);
    } catch (err) { if (alive.current && current.current === id) setError(formatApiError(err, 'Pièces jointes indisponibles.')); }
  }, []);
  useEffect(() => { alive.current = true; return () => { alive.current = false; fetchVersion.current += 1; }; }, []);
  useEffect(() => { current.current = sessionId; fetchVersion.current += 1; setItems([]); setSelected([]); setError(''); setArchiveReview(null); refresh(sessionId); }, [sessionId, refresh]);
  const upload = async files => {
    if (pending.current || !files.length) return;
    const incoming = Array.from(files);
    const hasZip = incoming.some(file => file.name.toLowerCase().endsWith('.zip'));
    if (hasZip && incoming.length !== 1) { setError('Ajoutez une archive ZIP à la fois, sans autres fichiers dans la même sélection.'); return; }
    if (!hasZip && incoming.length + selected.length > 5) { setError('Joignez au maximum 5 fichiers par demande.'); return; }
    if (incoming.some(file => !file.size || file.size > (hasZip ? 20 : 10) * 1024 * 1024)) { setError(hasZip ? 'Une archive ZIP doit peser entre 1 octet et 20 Mo.' : 'Chaque fichier doit peser entre 1 octet et 10 Mo.'); return; }
    const existingBytes = items.filter(item => selected.includes(item.id)).reduce((sum, item) => sum + item.size, 0);
    if (!hasZip && incoming.reduce((sum, file) => sum + file.size, existingBytes) > 20 * 1024 * 1024) { setError('Limite de 20 Mo par demande.'); return; }
    pending.current = true; setUploading(true); setError(''); setArchiveReview(null);
    let id = sessionId;
    try {
      if (!id) { const session = await ensureSession(); id = session?.id; }
      if (!id) throw new Error('Impossible d’ouvrir une discussion.');
      current.current = id;
      for (const file of incoming) {
        if (alive.current) setProgress(file.name);
        const { data } = await uploadOtoAttachment(id, file);
        if (alive.current && current.current === id) {
          if (data.archive_name && Array.isArray(data.items)) {
            setItems(previous => [...previous.filter(item => !data.items.some(added => added.id === item.id)), ...data.items]);
            setArchiveReview({ name: data.archive_name, count: data.items.length, skipped: data.skipped, batch: Date.now() });
          } else {
            setItems(previous => [...previous.filter(item => item.id !== data.id), data]);
            setSelected(previous => [...new Set([...previous, data.id])]);
          }
        }
      }
    } catch (err) { if (alive.current) setError(formatApiError(err, 'Le fichier n’a pas pu être joint.')); }
    finally {
      pending.current = false;
      if (alive.current) { setUploading(false); setProgress(''); await refresh(id); }
    }
  };
  const toggle = item => {
    setError('');
    if (selected.includes(item.id)) { setSelected(previous => previous.filter(id => id !== item.id)); return; }
    if (selected.length >= 5 || items.filter(file => selected.includes(file.id)).reduce((sum, file) => sum + file.size, item.size) > 20 * 1024 * 1024) {
      setError('Joignez au maximum 5 fichiers et 20 Mo par demande.'); return;
    }
    setSelected(previous => [...previous, item.id]);
  };
  return { items, selected, uploading, progress, error, archiveReview, upload, toggle, refresh: () => refresh(sessionId), clear: () => setSelected([]),
    remove: id => setSelected(previous => previous.filter(item => item !== id)) };
};